import React from "react";
import {
    Link
} from "@amzn/awsui-components-react/polaris";
import Constants from "../utils/Constants";
import FremontConstants from "../../../utils/Constants";
import {
    LinkServiceExpandableSection,
    LinkServiceSelect,
    TabularDisplayComponent
} from "../components/CommonComponents";
import HelperFunctions from "../common/HelperFunctions";
import {
    constructCellId,
    getLinkIdColumn,
    getLifecycleStateColumn,
    getAEndInterfaceColumn,
    getBEndInterfaceColumn,
    getAEndClientInterfaceColumn,
    getBEndClientInterfaceColumn,
    columnFromAttribute,
    columnWithLinkFromAttribute,
    getEncryptionCapabilityColumn,
    getEncryptionIntentColumn,
    getAttributesColumn,
    getConsumingLinksColumn,
    getClientPathNameColumn,
    getLinkTypeColumn
} from "./LinkColumnHelpers";

const { COLUMNS, ATTRIBUTES } = Constants;

export const getConsumesListFromAttributes = (attributes) => {
    const consumesAttribute = attributes
        .filter(attribute => attribute.key === Constants.CONSUMPTION_ATTRIBUTES.consumesList);
    if (consumesAttribute.length > 0) {
        return JSON.parse(consumesAttribute[0].value)
            .map(link => HelperFunctions.getIdentifierFromLinkInstance(link)).join(",");
    }
    return "";
};

export const getCommonLinkTableColumns = ({ linkType, editable }) => [
    getLinkIdColumn(linkType),
    getLifecycleStateColumn(linkType, editable),
    getAEndInterfaceColumn(linkType, COLUMNS.A_END_INTERFACE, editable),
    getBEndInterfaceColumn(linkType, COLUMNS.B_END_INTERFACE, editable),
    getEncryptionCapabilityColumn(linkType, editable),
    getEncryptionIntentColumn(linkType, editable),
    getAttributesColumn(linkType),
    getConsumingLinksColumn(linkType)
];

export const getCommonContainerTableColumns = props => (
    [
        {
            id: `${props.linkType}Id`,
            header: "Container ID",
            description: "Container ID",
            width: 330,
            minWidth: 330,
            cell: item =>
                <Link href={`${FremontConstants.MANGO_ROUTES.linkDetails}/${item.instanceId}`}>{item.instanceId}</Link>
        },
        {
            id: `${props.linkType}Type`,
            header: "Link Type",
            description: "Link Type",
            width: 165,
            cell: item => item.readableLinkType
        },
        {
            id: `${props.linkType}Lifecycle`,
            header: "Lifecycle State",
            description: "Lifecycle State",
            width: 150,
            cell: item => (
                props.editable ?
                    <LinkServiceSelect
                        id={
                            constructCellId(item.readableLinkType, item.instanceId, Constants.ATTRIBUTES.lifecycleState)
                        }
                        options={HelperFunctions.createSelectedOptions(Object.values(Constants.LIFECYCLE_STATES))}
                        onChange={item.handleLinkChange}
                        selectedOption={HelperFunctions.createSelectedOption(
                            item.lifecycleState
                        )}
                    />
                    : item.lifecycleState
            )
        },
        {
            id: `${props.linkType}Attributes`,
            header: "Attributes",
            description: "Attributes",
            cell: item =>
                (
                    <LinkServiceExpandableSection
                        headerText="Expand attributes"
                    >
                        <TabularDisplayComponent
                            keyValueFields={item.attributesToDisplay}
                            inEditMode={props.editable}
                            idPrefix={constructCellId(
                                item.readableLinkType,
                                item.instanceId,
                                Constants.ATTRIBUTES.attributesToDisplay
                            )}
                            onChange={item.handleLinkChange}
                        />
                    </LinkServiceExpandableSection>
                )
        },
        {
            id: `${props.linkType}ConsumingLinks`,
            header: "Consuming Links",
            description: "Consuming Links",
            cell: item => getConsumesListFromAttributes(item.attributesToDisplay)
        }
    ]
);

export function getPassiveToPassiveTableColumns(editable) {
    const linkType = Constants.LINK_TYPES.passiveToPassive;

    return [
        getLinkIdColumn(linkType),
        columnFromAttribute(linkType, COLUMNS.FIBER_ID, ATTRIBUTES.fibre_id, editable),
        getLifecycleStateColumn(linkType, editable),
        columnWithLinkFromAttribute(linkType, COLUMNS.CM_OR_TT_URL, ATTRIBUTES.cm_or_tt_url, editable),
        columnFromAttribute(linkType, COLUMNS.A_END_DARK_FIBER_HO, ATTRIBUTES.a_end_dark_fibre_handoff, editable),
        columnFromAttribute(linkType, COLUMNS.B_END_DARK_FIBER_HO, ATTRIBUTES.b_end_dark_fibre_handoff, editable),
        columnFromAttribute(linkType, COLUMNS.STYX_SPAN_NAME, ATTRIBUTES.styx_span_name),
        columnFromAttribute(linkType, COLUMNS.A_END_CROSS, ATTRIBUTES.a_end_cross_connection, editable),
        columnFromAttribute(linkType, COLUMNS.B_END_CROSS, ATTRIBUTES.b_end_cross_connection, editable),
        columnFromAttribute(linkType, COLUMNS.VENDOR_NAME, ATTRIBUTES.fibre_vendor, editable),
        columnFromAttribute(linkType, COLUMNS.FIBER_SERVICE_ID, ATTRIBUTES.fibre_service_id, editable),
        columnFromAttribute(linkType, COLUMNS.AMAZON_FIBER_PAIR_ID, ATTRIBUTES.amazon_fibre_pair_id),
        columnFromAttribute(linkType, COLUMNS.AMAZON_SPAN_ID, ATTRIBUTES.amazon_span_id),
        columnFromAttribute(linkType, COLUMNS.MAINTENANCE_GROUP, ATTRIBUTES.maintenance_group, editable),
        columnFromAttribute(linkType, COLUMNS.SOL_LOSS_1550_NM, ATTRIBUTES.sol_loss_1550_nm, editable),
        columnFromAttribute(linkType, COLUMNS.OPTICAL_DISTANCE, ATTRIBUTES.optical_distance_km, editable),
        columnFromAttribute(linkType, COLUMNS.FIBER_TYPE, ATTRIBUTES.styx_fiber_glass_type, editable),
        columnWithLinkFromAttribute(linkType, COLUMNS.VENDOR_URL, ATTRIBUTES.vendor_url, editable),
        getEncryptionCapabilityColumn(linkType, editable),
        getEncryptionIntentColumn(linkType, editable),
        getAttributesColumn(linkType),
        getConsumingLinksColumn(linkType)
    ];
}

export function getMuxToMuxTableColumns(editable) {
    const linkType = Constants.LINK_TYPES.muxToMux;

    return [
        getLinkIdColumn(linkType),
        // OTS ID should never be editable
        columnFromAttribute(linkType, COLUMNS.OTS_ID, ATTRIBUTES.user_provided_id, false, 175),
        getLifecycleStateColumn(linkType, editable),
        columnWithLinkFromAttribute(linkType, COLUMNS.CM_OR_TT_URL, ATTRIBUTES.cm_or_tt_url, editable),
        columnFromAttribute(linkType, COLUMNS.A_END_DC, ATTRIBUTES.a_end_dc, editable, 100),
        columnFromAttribute(linkType, COLUMNS.Z_END_DC, ATTRIBUTES.z_end_dc, editable, 100),
        columnFromAttribute(linkType, COLUMNS.A_B_DEGREE, ATTRIBUTES.a_b_degree, editable, 125),
        getAEndInterfaceColumn(linkType, COLUMNS.A_END_LINE_PORT, editable),
        getBEndInterfaceColumn(linkType, COLUMNS.B_END_LINE_PORT, editable),
        getEncryptionCapabilityColumn(linkType, editable),
        getEncryptionIntentColumn(linkType, editable),
        getAttributesColumn(linkType),
        getConsumingLinksColumn(linkType)
    ];
}

export function getRouterToRouterTableColumns(editable) {
    const linkType = Constants.LINK_TYPES.routerToRouter;

    return [
        getLinkIdColumn(linkType),
        getLifecycleStateColumn(linkType, editable),
        getClientPathNameColumn(linkType),
        columnFromAttribute(linkType, COLUMNS.FABRIC, ATTRIBUTES.fabric, editable, 80),
        getAEndInterfaceColumn(linkType, COLUMNS.A_END_FABRIC_INTERFACE, editable),
        getAEndClientInterfaceColumn(linkType, editable),
        getBEndInterfaceColumn(linkType, COLUMNS.B_END_FABRIC_INTERFACE, editable),
        getBEndClientInterfaceColumn(linkType, editable),
        columnFromAttribute(linkType, COLUMNS.CAPACITY, ATTRIBUTES.capacity, editable),
        getEncryptionCapabilityColumn(linkType, editable),
        getEncryptionIntentColumn(linkType, editable),
        getAttributesColumn(linkType),
        getConsumingLinksColumn(linkType)
    ];
}

export function getTrunkToTrunkTableColumns(editable) {
    const linkType = Constants.LINK_TYPES.trunkToTrunk;

    return [
        getLinkIdColumn(linkType),
        getLifecycleStateColumn(linkType, editable),
        columnWithLinkFromAttribute(linkType, COLUMNS.CM_OR_TT_URL, ATTRIBUTES.cm_or_tt_url, editable),
        columnFromAttribute(linkType, COLUMNS.TYPE, ATTRIBUTES.type, editable),
        getAEndInterfaceColumn(linkType, COLUMNS.A_END_TRUNK_PORT, editable),
        getBEndInterfaceColumn(linkType, COLUMNS.B_END_TRUNK_PORT, editable),
        columnFromAttribute(linkType, COLUMNS.WAVELENGTH, ATTRIBUTES.wavelength, editable),
        columnFromAttribute(linkType, COLUMNS.CHANNEL_CAPACITY_GBPS, ATTRIBUTES.channel_capacity_gbps, editable),
        columnFromAttribute(linkType, COLUMNS.A_END_CROSS, ATTRIBUTES.a_end_cross_connection, editable),
        columnFromAttribute(linkType, COLUMNS.B_END_CROSS, ATTRIBUTES.b_end_cross_connection, editable),
        getEncryptionCapabilityColumn(linkType, editable),
        getEncryptionIntentColumn(linkType, editable),
        getAttributesColumn(linkType),
        getConsumingLinksColumn(linkType)
    ];
}

export const getOmsToOmsTableColumns = editable =>
    getCommonLinkTableColumns({
        linkType: Constants.LINK_TYPES.omsToOms,
        editable
    });
export const getPowerMuxToPowerMuxTableColumns = editable =>
    getCommonLinkTableColumns({
        linkType: Constants.LINK_TYPES.powerMuxToPowerMux,
        editable
    });
export const getOduToOduTableColumns = editable =>
    getCommonLinkTableColumns({
        linkType: Constants.LINK_TYPES.oduToOdu,
        editable
    });
export const getRouterToDwdmTableColumns = editable =>
    getCommonLinkTableColumns({
        linkType: Constants.LINK_TYPES.routerToDWDM,
        editable
    });
export const getClientToClientTableColumns = editable =>
    getCommonLinkTableColumns({
        linkType: Constants.LINK_TYPES.clientToClient,
        editable
    });

export const getProtectionContainerColumns = editable =>
    getCommonContainerTableColumns({
        linkType: Constants.CONTAINER_TYPES.protectionContainer,
        editable
    });
export const getParallelContainerColumns = editable =>
    getCommonContainerTableColumns({
        linkType: Constants.CONTAINER_TYPES.parallelContainer,
        editable
    });
export const getEncryptionToEncryptionColumns = editable =>
    getCommonLinkTableColumns({
        linkType: Constants.LINK_TYPES.encryptionToEncryption,
        editable
    });
export const getRouterToEncryptionColumns = editable =>
    getCommonLinkTableColumns({
        linkType: Constants.LINK_TYPES.routerToEncryption,
        editable
    });
export const getEncryptionToDWDMColumns = editable =>
    getCommonLinkTableColumns({
        linkType: Constants.LINK_TYPES.encryptionToDWDM,
        editable
    });
export const getIntraDcRouterToIntraDcRouterColumns = editable =>
    getCommonLinkTableColumns({
        linkType: Constants.LINK_TYPES.intraDcRouterToIntraDcRouter,
        editable
    });
export const getIntraDcRouterToIntraDcPassiveColumns = editable =>
    getCommonLinkTableColumns({
        linkType: Constants.LINK_TYPES.intraDcRouterToIntraDcPassive,
        editable
    });
export const getIntraDcPassiveToIntraDcPassiveColumns = editable =>
    getCommonLinkTableColumns({
        linkType: Constants.LINK_TYPES.intraDcPassiveToIntraDcPassive,
        editable
    });
export const getIntraOlsClientToIntraOlsClientColumns = (editable) => {
    const linkType = Constants.LINK_TYPES.intraOlsClientToIntraOlsClient;

    return [
        getLinkIdColumn(linkType),
        getLifecycleStateColumn(linkType, editable),
        getAEndInterfaceColumn(linkType, COLUMNS.A_END_OPTICAL_CLIENT_PORT, editable),
        getBEndInterfaceColumn(linkType, COLUMNS.B_END_OPTICAL_CLIENT_PORT, editable),
        getEncryptionCapabilityColumn(linkType, editable),
        getEncryptionIntentColumn(linkType, editable),
        getAttributesColumn(linkType),
        getConsumingLinksColumn(linkType)
    ];
};

// These ColumnDefinitions are just for download purposes
export function getDownloadAblePassiveToPassiveTableColumns() {
    const linkType = Constants.LINK_TYPES.passiveToPassive;
    return [
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.fibre_id, ATTRIBUTES.fibre_id),
        getLifecycleStateColumn(linkType, false),
        columnWithLinkFromAttribute(
            linkType, Constants.DOWNLOADABLE_COLUMNS.cm_or_tt_url, ATTRIBUTES.cm_or_tt_url, false
        ),
        columnFromAttribute(
            linkType, Constants.DOWNLOADABLE_COLUMNS.a_end_dark_fibre_handoff, ATTRIBUTES.a_end_dark_fibre_handoff
        ),
        columnFromAttribute(
            linkType, Constants.DOWNLOADABLE_COLUMNS.b_end_dark_fibre_handoff, ATTRIBUTES.b_end_dark_fibre_handoff
        ),
        columnFromAttribute(
            linkType, Constants.DOWNLOADABLE_COLUMNS.a_end_cross_connection, ATTRIBUTES.a_end_cross_connection
        ),
        columnFromAttribute(
            linkType, Constants.DOWNLOADABLE_COLUMNS.b_end_cross_connection, ATTRIBUTES.b_end_cross_connection
        ),
        columnFromAttribute(
            linkType, Constants.DOWNLOADABLE_COLUMNS.fibre_vendor, ATTRIBUTES.fibre_vendor
        ),
        columnFromAttribute(
            linkType, Constants.DOWNLOADABLE_COLUMNS.fibre_service_id, ATTRIBUTES.fibre_service_id
        ),
        columnFromAttribute(
            linkType, Constants.DOWNLOADABLE_COLUMNS.maintenance_group, ATTRIBUTES.maintenance_group
        ),
        columnFromAttribute(
            linkType, Constants.DOWNLOADABLE_COLUMNS.sol_loss_1550_nm, ATTRIBUTES.sol_loss_1550_nm
        ),
        columnFromAttribute(
            linkType, Constants.DOWNLOADABLE_COLUMNS.optical_distance_km, ATTRIBUTES.optical_distance_km
        ),
        columnWithLinkFromAttribute(
            linkType, Constants.DOWNLOADABLE_COLUMNS.vendor_url, ATTRIBUTES.vendor_url, false
        )
    ];
}
export function getDownloadAbleMuxToMuxTableColumns() {
    const linkType = Constants.LINK_TYPES.muxToMux;
    return [
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.ots_id, ATTRIBUTES.user_provided_id),
        getLifecycleStateColumn(linkType, false),
        columnWithLinkFromAttribute(
            linkType, Constants.DOWNLOADABLE_COLUMNS.cm_or_tt_url, ATTRIBUTES.cm_or_tt_url, false
        ),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.a_end_dc, ATTRIBUTES.a_end_dc),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.b_end_dc, ATTRIBUTES.z_end_dc),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.a_b_degree, ATTRIBUTES.a_b_degree),
        getAEndInterfaceColumn(linkType, Constants.DOWNLOADABLE_COLUMNS.a_end_line_port),
        getBEndInterfaceColumn(linkType, Constants.DOWNLOADABLE_COLUMNS.b_end_line_port),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.fibre_trail, ATTRIBUTES.fibre_trail),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.a_ops_dwdm_port, ATTRIBUTES.a_ops_dwdm_port),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.b_ops_dwdm_port, ATTRIBUTES.b_ops_dwdm_port),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.a_end_line_port_2, ATTRIBUTES.a_end_line_port_2),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.b_end_line_port_2, ATTRIBUTES.b_end_line_port_2),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.a_rfts_filter, ATTRIBUTES.a_rfts_filter),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.b_rfts_filter, ATTRIBUTES.b_rfts_filter),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.a_rfts_in_port_1, ATTRIBUTES.a_rfts_in_port_1),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.b_rfts_in_port_1, ATTRIBUTES.b_rfts_in_port_1),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.a_rfts_out_port_1, ATTRIBUTES.a_rfts_out_port_1),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.b_rfts_out_port_1, ATTRIBUTES.b_rfts_out_port_1),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.a_rfts_in_port_2, ATTRIBUTES.a_rfts_in_port_2),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.b_rfts_in_port_2, ATTRIBUTES.b_rfts_in_port_2),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.a_rfts_out_port_2, ATTRIBUTES.a_rfts_out_port_2),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.b_rfts_out_port_2, ATTRIBUTES.b_rfts_out_port_2),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.a_pluggable, ATTRIBUTES.a_pluggable),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.b_pluggable, ATTRIBUTES.b_pluggable),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.a_connector_type, ATTRIBUTES.a_connector_type),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.b_connector_type, ATTRIBUTES.b_connector_type),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.comment, ATTRIBUTES.comment)
    ];
}

export function getDownloadAbleTrunkToTrunkTableColumns() {
    const linkType = Constants.LINK_TYPES.trunkToTrunk;
    return [
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.och_id, ATTRIBUTES.user_provided_id),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.och_type, ATTRIBUTES.type),
        getAEndInterfaceColumn(linkType, Constants.DOWNLOADABLE_COLUMNS.a_end_trunk_port),
        getBEndInterfaceColumn(linkType, Constants.DOWNLOADABLE_COLUMNS.b_end_trunk_port),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.wavelength, ATTRIBUTES.wavelength),
        columnFromAttribute(
            linkType, Constants.DOWNLOADABLE_COLUMNS.channel_capacity_gbps, ATTRIBUTES.channel_capacity_gbps
        ),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.name, ATTRIBUTES.name),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.provider, ATTRIBUTES.provider),
        columnFromAttribute(
            linkType, Constants.DOWNLOADABLE_COLUMNS.a_end_cross_connection, ATTRIBUTES.a_end_cross_connection
        ),
        columnFromAttribute(
            linkType, Constants.DOWNLOADABLE_COLUMNS.b_end_cross_connection, ATTRIBUTES.b_end_cross_connection
        ),
        columnWithLinkFromAttribute(
            linkType, Constants.DOWNLOADABLE_COLUMNS.cm_or_tt_url, ATTRIBUTES.cm_or_tt_url, false
        ),
        getLifecycleStateColumn(linkType, false),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.ots_trail, ATTRIBUTES.ots_trail),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.a_mux_dwdm_port, ATTRIBUTES.a_mux_dwdm_port),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.b_mux_dwdm_port, ATTRIBUTES.b_mux_dwdm_port)
    ];
}

export function getDownloadAbleRouterToRouterTableColumns() {
    const linkType = Constants.LINK_TYPES.routerToRouter;
    return [
        getClientPathNameColumn(linkType),
        getLifecycleStateColumn(linkType, false),
        columnWithLinkFromAttribute(
            linkType, Constants.DOWNLOADABLE_COLUMNS.cm_or_tt_url, ATTRIBUTES.cm_or_tt_url, false
        ),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.fabric, ATTRIBUTES.fabric),
        columnFromAttribute(
            linkType, Constants.DOWNLOADABLE_COLUMNS.client_path_capacity_Gbps, ATTRIBUTES.capacity
        ),
        getAEndInterfaceColumn(linkType, Constants.DOWNLOADABLE_COLUMNS.a_end_fabric_port, false),
        columnWithLinkFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.a_end_po, ATTRIBUTES.a_end_po, false),
        getAEndClientInterfaceColumn(linkType, false),
        getBEndInterfaceColumn(linkType, Constants.DOWNLOADABLE_COLUMNS.b_end_fabric_port, false),
        columnWithLinkFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.b_end_po, ATTRIBUTES.b_end_po, false),
        getBEndClientInterfaceColumn(linkType, false),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.och_trail, ATTRIBUTES.och_trail),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.fiber_path, ATTRIBUTES.fiber_path),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.a_optic_type, ATTRIBUTES.a_optic_type),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.a_connector_type, ATTRIBUTES.a_connector_type),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.a_fiber_type, ATTRIBUTES.a_fiber_type),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.b_fiber_type, ATTRIBUTES.b_fiber_type),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.b_connector_type, ATTRIBUTES.b_connector_type),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.b_optic_type, ATTRIBUTES.b_optic_type),
        columnFromAttribute(
            linkType, Constants.DOWNLOADABLE_COLUMNS.a_client_dwdm_optic_type, ATTRIBUTES.a_client_dwdm_optic_type
        ),
        columnFromAttribute(
            linkType, Constants.DOWNLOADABLE_COLUMNS.b_client_dwdm_optic_type, ATTRIBUTES.b_client_dwdm_optic_type
        )
    ];
}

export function getDownloadAbleClientToClientTableColumns() {
    const linkType = Constants.LINK_TYPES.clientToClient;
    return [
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.client_path_name, ATTRIBUTES.client_path_name),
        getAEndInterfaceColumn(linkType, Constants.DOWNLOADABLE_COLUMNS.a_end_client_port, false),
        getBEndInterfaceColumn(linkType, Constants.DOWNLOADABLE_COLUMNS.b_end_client_port, false),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.a_optic_type, ATTRIBUTES.a_optic_type),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.b_optic_type, ATTRIBUTES.b_optic_type)
    ];
}

// Additional downloadable methods for missing link types
export function getDownloadAbleOmsToOmsTableColumns() {
    const linkType = Constants.LINK_TYPES.omsToOms;
    return [
        getLinkIdColumn(linkType),
        getLifecycleStateColumn(linkType, false),
        columnWithLinkFromAttribute(
            linkType, Constants.DOWNLOADABLE_COLUMNS.cm_or_tt_url, ATTRIBUTES.cm_or_tt_url, false
        ),
        getAEndInterfaceColumn(linkType, Constants.DOWNLOADABLE_COLUMNS.a_end_interface, false),
        getBEndInterfaceColumn(linkType, Constants.DOWNLOADABLE_COLUMNS.b_end_interface, false),
        getEncryptionCapabilityColumn(linkType, false),
        getEncryptionIntentColumn(linkType, false)
    ];
}

export function getDownloadAblePowerMuxToPowerMuxTableColumns() {
    const linkType = Constants.LINK_TYPES.powerMuxToPowerMux;
    return [
        getLinkIdColumn(linkType),
        getLifecycleStateColumn(linkType, false),
        columnWithLinkFromAttribute(
            linkType, Constants.DOWNLOADABLE_COLUMNS.cm_or_tt_url, ATTRIBUTES.cm_or_tt_url, false
        ),
        getAEndInterfaceColumn(linkType, Constants.DOWNLOADABLE_COLUMNS.a_end_interface, false),
        getBEndInterfaceColumn(linkType, Constants.DOWNLOADABLE_COLUMNS.b_end_interface, false),
        getEncryptionCapabilityColumn(linkType, false),
        getEncryptionIntentColumn(linkType, false)
    ];
}

export function getDownloadAbleOduToOduTableColumns() {
    const linkType = Constants.LINK_TYPES.oduToOdu;
    return [
        getLinkIdColumn(linkType),
        getLifecycleStateColumn(linkType, false),
        columnWithLinkFromAttribute(
            linkType, Constants.DOWNLOADABLE_COLUMNS.cm_or_tt_url, ATTRIBUTES.cm_or_tt_url, false
        ),
        getAEndInterfaceColumn(linkType, Constants.DOWNLOADABLE_COLUMNS.a_end_trunk_port, false),
        getBEndInterfaceColumn(linkType, Constants.DOWNLOADABLE_COLUMNS.b_end_trunk_port, false),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.wavelength, ATTRIBUTES.wavelength),
        columnFromAttribute(
            linkType, Constants.DOWNLOADABLE_COLUMNS.channel_capacity_gbps, ATTRIBUTES.channel_capacity_gbps
        ),
        getEncryptionCapabilityColumn(linkType, false),
        getEncryptionIntentColumn(linkType, false)
    ];
}

export function getDownloadAbleEncryptionToEncryptionTableColumns() {
    const linkType = Constants.LINK_TYPES.encryptionToEncryption;
    return [
        getLinkIdColumn(linkType),
        getLifecycleStateColumn(linkType, false),
        columnWithLinkFromAttribute(
            linkType, Constants.DOWNLOADABLE_COLUMNS.cm_or_tt_url, ATTRIBUTES.cm_or_tt_url, false
        ),
        getAEndInterfaceColumn(linkType, Constants.DOWNLOADABLE_COLUMNS.a_end_interface, false),
        getBEndInterfaceColumn(linkType, Constants.DOWNLOADABLE_COLUMNS.b_end_interface, false),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.capacity, ATTRIBUTES.capacity),
        getEncryptionCapabilityColumn(linkType, false),
        getEncryptionIntentColumn(linkType, false)
    ];
}

export function getDownloadAbleRouterToEncryptionTableColumns() {
    const linkType = Constants.LINK_TYPES.routerToEncryption;
    return [
        getLinkIdColumn(linkType),
        getLifecycleStateColumn(linkType, false),
        columnWithLinkFromAttribute(
            linkType, Constants.DOWNLOADABLE_COLUMNS.cm_or_tt_url, ATTRIBUTES.cm_or_tt_url, false
        ),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.fabric, ATTRIBUTES.fabric),
        getAEndInterfaceColumn(linkType, Constants.DOWNLOADABLE_COLUMNS.a_end_fabric_port, false),
        getAEndClientInterfaceColumn(linkType, false),
        getBEndInterfaceColumn(linkType, Constants.DOWNLOADABLE_COLUMNS.b_end_interface, false),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.capacity, ATTRIBUTES.capacity),
        getEncryptionCapabilityColumn(linkType, false),
        getEncryptionIntentColumn(linkType, false)
    ];
}

export function getDownloadAbleEncryptionToDWDMTableColumns() {
    const linkType = Constants.LINK_TYPES.encryptionToDWDM;
    return [
        getLinkIdColumn(linkType),
        getLifecycleStateColumn(linkType, false),
        columnWithLinkFromAttribute(
            linkType, Constants.DOWNLOADABLE_COLUMNS.cm_or_tt_url, ATTRIBUTES.cm_or_tt_url, false
        ),
        getAEndInterfaceColumn(linkType, Constants.DOWNLOADABLE_COLUMNS.a_end_interface, false),
        getBEndInterfaceColumn(linkType, Constants.DOWNLOADABLE_COLUMNS.b_end_trunk_port, false),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.capacity, ATTRIBUTES.capacity),
        getEncryptionCapabilityColumn(linkType, false),
        getEncryptionIntentColumn(linkType, false)
    ];
}

export function getDownloadAbleLightInterfaceUnitTableColumns() {
    const linkType = Constants.LINK_TYPES.lightInterfaceUnit;
    return [
        getLinkIdColumn(linkType),
        getLifecycleStateColumn(linkType, false),
        columnWithLinkFromAttribute(
            linkType, Constants.DOWNLOADABLE_COLUMNS.cm_or_tt_url, ATTRIBUTES.cm_or_tt_url, false
        ),
        getAEndInterfaceColumn(linkType, Constants.DOWNLOADABLE_COLUMNS.a_end_interface, false),
        getBEndInterfaceColumn(linkType, Constants.DOWNLOADABLE_COLUMNS.b_end_interface, false),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.capacity, ATTRIBUTES.capacity),
        getEncryptionCapabilityColumn(linkType, false),
        getEncryptionIntentColumn(linkType, false)
    ];
}

export function getDownloadAbleIntraDcRouterToIntraDcRouterTableColumns() {
    const linkType = Constants.LINK_TYPES.intraDcRouterToIntraDcRouter;
    return [
        getLinkIdColumn(linkType),
        getLinkTypeColumn(linkType),
        getLifecycleStateColumn(linkType, false),
        getAEndInterfaceColumn(linkType, Constants.DOWNLOADABLE_COLUMNS.a_end_interface, false),
        getBEndInterfaceColumn(linkType, Constants.DOWNLOADABLE_COLUMNS.b_end_interface, false),
        columnFromAttribute(linkType, Constants.DOWNLOADABLE_COLUMNS.capacity, ATTRIBUTES.capacity),
        getEncryptionCapabilityColumn(linkType, false),
        getEncryptionIntentColumn(linkType, false)
    ];
}

export function getDownloadAbleIntraDcRouterToIntraDcPassiveTableColumns() {
    const linkType = Constants.LINK_TYPES.intraDcRouterToIntraDcPassive;
    return [
        getLinkIdColumn(linkType),
        getLifecycleStateColumn(linkType, false),
        getAEndInterfaceColumn(linkType, Constants.DOWNLOADABLE_COLUMNS.a_end_interface, false),
        getBEndInterfaceColumn(linkType, Constants.DOWNLOADABLE_COLUMNS.b_end_interface, false),
        getEncryptionCapabilityColumn(linkType, false),
        getEncryptionIntentColumn(linkType, false)
    ];
}

export function getDownloadAbleIntraDcPassiveToIntraDcPassiveTableColumns() {
    const linkType = Constants.LINK_TYPES.intraDcPassiveToIntraDcPassive;
    return [
        getLinkIdColumn(linkType),
        getLifecycleStateColumn(linkType, false),
        getAEndInterfaceColumn(linkType, Constants.DOWNLOADABLE_COLUMNS.a_end_interface, false),
        getBEndInterfaceColumn(linkType, Constants.DOWNLOADABLE_COLUMNS.b_end_interface, false),
        getEncryptionCapabilityColumn(linkType, false),
        getEncryptionIntentColumn(linkType, false)
    ];
}

export function getDownloadAbleIntraOlsClientToIntraOlsClientTableColumns() {
    const linkType = Constants.LINK_TYPES.intraOlsClientToIntraOlsClient;
    return [
        getLinkIdColumn(linkType),
        getLifecycleStateColumn(linkType, false),
        getAEndInterfaceColumn(linkType, COLUMNS.A_END_OPTICAL_CLIENT_PORT, false),
        getBEndInterfaceColumn(linkType, COLUMNS.B_END_OPTICAL_CLIENT_PORT, false),
        getEncryptionCapabilityColumn(linkType, false),
        getEncryptionIntentColumn(linkType, false)
    ];
}
